import { StreamingMarkdown } from "@storytell/markdown";
import { RichTextEditor, richTextToProseMirror } from "@storytell/rich-text";
import { clsx } from "clsx";
import {
  type Component,
  Show,
  createEffect,
  createMemo,
  createRenderEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { isServer } from "solid-js/web";
import { artifactMarkdownOptions } from "~/config/markdownRenderer";
import type { ArtifactState } from "~/stores/ArtifactStore";
import { useAppUI, ExtendedPaneMode } from "~/components/layouts/minimal/AppUIContext";
import { subscribeToBlockDeltas } from "@storytell/streaming";
import { dispatchGlobalEvent } from "@storytell/events";

interface ArtifactViewerProps {
  artifact: ArtifactState;
  isActive: boolean;
  class?: string;
}

export const ArtifactViewer: Component<ArtifactViewerProps> = (props) => {
  const { actions } = useAppUI();
  // Only render content when active for performance
  const shouldRenderContent = createMemo(() => props.isActive);

  const handleClose = () => {
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
  };

  const handlePublish = () => {
    // TODO: Implement publish functionality
    console.log("Publish artifact:", props.artifact.id);
  };

  return (
    <div class={clsx("w-full h-full bg-tile relative", props.isActive ? "block" : "hidden", props.class)}>
      <Show when={shouldRenderContent()}>
        {/* Action buttons positioned at top right */}
        <div class="absolute top-6 right-6 z-10 flex items-center gap-2">
          <button
            type="button"
            class="px-3 py-1.5 text-sm bg-panel hover:bg-panel-hover text-secondary-text border border-muted-border rounded transition-colors duration-150"
            onClick={handlePublish}
            title="Publish artifact"
          >
            Save to My Knowledge
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm bg-panel hover:bg-panel-hover text-secondary-text border border-muted-border rounded transition-colors duration-150"
            onClick={() => copyToClipboard(props.artifact)}
            title="Copy raw markdown to clipboard"
          >
            Copy
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm bg-panel hover:bg-panel-hover text-secondary-text border border-muted-border rounded transition-colors duration-150"
            onClick={() => downloadArtifact(props.artifact)}
            title="Download artifact"
          >
            Download
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm bg-panel hover:bg-panel-hover text-secondary-text border border-muted-border rounded transition-colors duration-150"
            onClick={handlePublish}
            title="Publish artifact"
          >
            Share
          </button>
          <button
            type="button"
            class="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-150"
            onClick={handleClose}
            title="Close panel"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Artifact content - full height scrollable area */}
        <div class="h-full overflow-auto">
          <UnifiedArtifactRenderer artifact={props.artifact} />
        </div>
      </Show>
    </div>
  );
};

// Streaming markdown component optimized for artifact content
const StreamingArtifactMarkdown: Component<{ artifact: ArtifactState }> = (props) => {
  let markdownRef: { append: (text: string) => void; reset: () => void } | undefined;
  let lastContentLength = 0;
  const id = createMemo(() => props.artifact.id);

  createEffect(() => {
    if (!id()) return;
    const artifactId = id();
    const unsubscribe = subscribeToBlockDeltas(artifactId, (delta) => {
      if (delta.length <= lastContentLength) {
        markdownRef?.reset();
      }

      markdownRef?.append(delta);
    });
    onCleanup(() => {
      unsubscribe();
    });
  });
  // Handle artifact content updates using append method for better performance
  // createEffect(() => {
  //   const currentContent = (props.artifact.content as string) || "";
  //   const currentContentLength = currentContent.length;

  //   if (markdownRef) {
  //     if (currentContentLength === 0) {
  //       // Reset if content is empty
  //       markdownRef.reset();
  //       lastContentLength = 0;
  //     } else if (currentContentLength > lastContentLength) {
  //       // Append only the new content
  //       const newContent = currentContent.substring(lastContentLength);
  //       markdownRef.append(newContent);
  //       lastContentLength = currentContentLength;
  //     } else if (currentContentLength < lastContentLength) {
  //       // Content was truncated, reset and append all content
  //       markdownRef.reset();
  //       if (currentContent) {
  //         markdownRef.append(currentContent);
  //       }
  //       lastContentLength = currentContentLength;
  //     }
  //   }
  // });

  return (
    <StreamingMarkdown
      ref={markdownRef}
      onAppendReady={(append, reset) => {
        markdownRef = { append, reset };
        // Initialize with current content
        const initialContent = (props.artifact.content as string) || "";
        if (initialContent) {
          append(initialContent);
          lastContentLength = initialContent.length;
        }
      }}
    />
  );
};

// Unified renderer that handles all artifact types
const UnifiedArtifactRenderer: Component<{ artifact: ArtifactState }> = (props) => {
  const { state } = useAppUI();
  let scrollableStartPoint: HTMLDivElement | undefined;

  const [transitionToEditor, setTransitionToEditor] = createSignal(false);

  // Create memo to determine rendering strategy based on processing state and user preference
  const shouldUseRichText = createMemo(() => {
    // Must have rich text data available
    const hasRichText = props.artifact?.metadata?.richText;

    // Must be done processing (stop message received)
    const isDoneProcessing = props.artifact && !props.artifact.isProcessing;

    // User must have rich text replacement enabled (controllable via debug toggle)
    const userWantsRichText = state.useRichTextReplacement;

    return hasRichText && isDoneProcessing && userWantsRichText;
  });

  // For debugging, show what renderer is being used
  const rendererType = createMemo(() => {
    if (shouldUseRichText()) return "RichText";
    if (props.artifact.isProcessing) return "StreamingMarkdown (Processing)";
    return "StreamingMarkdown (Final)";
  });

  const getScrollableParent = () => {
    let current = scrollableStartPoint;
    while (current) {
      if (current.scrollTop > 0) {
        return current;
      }
      current = current.parentElement as HTMLDivElement;
    }
    return scrollableStartPoint;
  };

  return (
    <div class="h-full p-4" ref={scrollableStartPoint}>
      <div class="h-full overflow-auto relative pt-20 pb-100">
        <div class="flex h-full">
          {/* Left spacer - grows/shrinks more aggressively with panel width */}
          <div class="flex-[2] min-w-2 max-w-100" />

          {/* Content column - maintains readable width */}
          <div class="max-w-[90ch] w-full">
            {/* Always show streaming markdown when rich text is disabled or during transition */}
            <Show when={!shouldUseRichText() || !transitionToEditor()}>
              <StreamingArtifactMarkdown artifact={props.artifact} />
            </Show>

            {/* Only show rich text renderer when enabled and available */}
            <Show when={shouldUseRichText() && props.artifact.metadata?.richText}>
              <div classList={{ "absolute inset-0": !transitionToEditor() }}>
                <RichTextEditor
                  onRendered={() => {
                    const parent = getScrollableParent();
                    const scroll = parent?.scrollTop;
                    setTransitionToEditor(true);
                    requestAnimationFrame(() => {
                      parent?.scrollTo({ top: scroll, behavior: "instant" });
                    });
                  }}
                  doc={richTextToProseMirror(props.artifact.metadata?.richText!)}
                  onQuoteInPromptBar={(data) => {
                    dispatchGlobalEvent("quote-in-prompt-bar", data);
                  }}
                />
              </div>
            </Show>
          </div>

          {/* Right spacer - less aggressive scaling to provide padding */}
          <div class="flex-[1] min-w-2 max-w-100" />
        </div>
      </div>
    </div>
  );
};

// Utility functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const copyToClipboard = async (artifact: ArtifactState) => {
  if (isServer) return;

  try {
    const content = typeof artifact.content === "string" ? artifact.content : JSON.stringify(artifact.content, null, 2);
    await navigator.clipboard.writeText(content);
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
};

const downloadArtifact = (artifact: ArtifactState) => {
  if (isServer) return;

  try {
    const content = typeof artifact.content === "string" ? artifact.content : JSON.stringify(artifact.content, null, 2);

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.title.replace(/[^a-z0-9]/gi, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Failed to download artifact:", err);
  }
};
